import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Loader2, Plus } from "lucide-react";

import { listProjectRecords, type ProjectRecord, type ProjectRecordKind } from "@/api/development_suite";
import {
  developmentSuiteKeys,
  isDeniedError,
  retryDevelopmentSuiteRequest,
} from "@/components/development_suite/developmentSuiteQueries";
import { ListPager, RequestFailure } from "@/components/development_suite/DevelopmentSuiteStates";
import { ProjectRecordDialog, RECORD_KIND_COPY } from "@/components/development_suite/ProjectRecordDialog";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/useToast";

type DialogState = { record: ProjectRecord | null } | null;

export function ProjectRecordsSection({
  municipalityId,
  projectId,
  kind,
}: {
  municipalityId: number;
  projectId: number;
  kind: ProjectRecordKind;
}) {
  const copy = RECORD_KIND_COPY[kind];
  const headingId = `project-${kind}-heading`;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<DialogState>(null);

  const query = useQuery({
    queryKey: developmentSuiteKeys.records(municipalityId, projectId, kind, page),
    queryFn: () => listProjectRecords(municipalityId, projectId, kind, page),
    retry: retryDevelopmentSuiteRequest,
    placeholderData: (previous) => previous,
  });

  const handleSaved = (saved: ProjectRecord) => {
    const created = dialog?.record == null;
    setDialog(null);
    if (created) setPage(1);
    void queryClient.invalidateQueries({ queryKey: developmentSuiteKeys.project(municipalityId, projectId) });
    toast({ title: created ? `Added ${saved.title}` : `Saved ${saved.title}` });
  };

  const records = query.isError ? [] : (query.data?.records ?? []);
  const pagination = query.isError ? undefined : query.data?.pagination;

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={headingId} className="text-sm font-semibold">
          {copy.plural}
          {pagination ? (
            <span className="ml-2 font-normal text-muted-foreground">
              <span className="sr-only">: </span>
              {pagination.total_count}
            </span>
          ) : null}
        </h3>
        {isDeniedError(query.error) ? null : (
          <Button size="sm" variant="outline" onClick={() => setDialog({ record: null })}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            Add {copy.singular}
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading {copy.plural.toLowerCase()}…
        </p>
      ) : query.isError ? (
        <RequestFailure error={query.error} what={copy.plural} onRetry={() => void query.refetch()} />
      ) : records.length === 0 ? (
        <p className="rounded-md border border-dashed px-4 py-5 text-center text-sm text-muted-foreground">
          No {copy.plural.toLowerCase()} yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <Table aria-labelledby={headingId} className="table-fixed">
            <colgroup>
              <col />
              <col className="w-36 sm:w-44" />
              <col className="hidden w-32 sm:table-column" />
              <col className="w-10" />
            </colgroup>
            <TableHeader className="bg-muted/40">
              <TableRow className="hover:bg-transparent">
                <TableHead scope="col" className="px-4">
                  Title
                </TableHead>
                <TableHead scope="col" className="px-3">
                  Reference
                </TableHead>
                <TableHead scope="col" className="hidden px-3 sm:table-cell">
                  Added
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Open {copy.singular}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((record) => (
                <TableRow
                  key={record.id}
                  className="group cursor-pointer focus-within:bg-muted/50"
                  onClick={() => setDialog({ record })}
                >
                  <TableCell className="px-4 py-3">
                    {/* The row handles the click; Enter on this button bubbles to it. */}
                    <button
                      type="button"
                      className="w-full rounded-sm text-left font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="line-clamp-2 break-words">{record.title}</span>
                    </button>
                  </TableCell>
                  <TableCell className="truncate px-3 py-3 text-muted-foreground">
                    {record.external_reference ?? "None"}
                  </TableCell>
                  <TableCell className="hidden whitespace-nowrap px-3 py-3 text-xs text-muted-foreground sm:table-cell">
                    {formatRelativeTime(record.created_at)}
                  </TableCell>
                  <TableCell className="pr-4 text-muted-foreground group-focus-within:text-foreground group-hover:text-foreground">
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {pagination ? <ListPager pagination={pagination} label={copy.plural} onPageChange={setPage} /> : null}

      {dialog ? (
        <ProjectRecordDialog
          municipalityId={municipalityId}
          projectId={projectId}
          kind={kind}
          record={dialog.record}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
        />
      ) : null}
    </section>
  );
}
